import { atom } from 'infrastructure/state/jotai.wrapper'
import {
  googleUserAtom,
  getFileInfo,
  getSheet,
  GDriveFileInfo,
  GDriveFileWithPath,
} from 'infrastructure/googleDrive'
import {
  ingestedFilesAtom,
  sourceFilesAtom,
} from '../../components/ingestion/atoms'
import { htmlDocForUnitUrl, sortUnitFiles } from './utility'
import { ICourseNode } from '../../components/ingestion/IngestedTree'
import { ParsedGoogleDocType } from './types'
import { isMaterialsFile } from './gDriveUtils'
import { parseCsvData, stringOrNumberSort } from '../../components/Helpers'
import { getDocMetadata } from './html'

export const userMessagesAtom = atom<string[]>([])

export const selectedCourseAtom = atom('')

export const parsedAtom = atom<ICourseNode | undefined>(undefined)

export const sortedS3Files = atom(async (get) => {
  const files = await get(sourceFilesAtom)
  const filtered = files
    .filter(({ URL }) => URL.endsWith('.html'))
    .map((item) => ({ ...item, name: item.key, isFile: true }))
  return sortUnitFiles(filtered)
})

export const pairedFilesAtom = atom(async (get) => {
  const ingestedFiles = await get(ingestedFilesAtom)
  const s3Files = await get(sortedS3Files)

  return Object.keys(s3Files).reduce<
    Record<
      string,
      { URL: string; key: string; last_modified: string } | undefined
    >
  >((acc, key) => {
    return {
      ...acc,
      [key]: ingestedFiles.find(
        ({ key: ingestedKey }) => ingestedKey.split('.')[0] === key,
      ),
    }
  }, {})
})

const contentFolders = [
  '1Tm-t3DmWFwWwS5cBN-0cF1Y6ToNPuCps',
  '1Xp3b12vjhsUG8A59uP-pvOO_GiMj5mzz',
  '15dwPMO1iYKXvl9r6kFEE-ojnrbovvo8G',
]

export const gDriveOptionsAtom = atom(async (get) => {
  const { credential } = get(googleUserAtom)
  if (!credential) return
  return Promise.all<GDriveFileInfo>(
    contentFolders.map((fid) => {
      return getFileInfo(credential.accessToken, fid) as Promise<GDriveFileInfo>
    }),
  )
})

export const sourceFilesForSelectedCourseAtom = atom(async (get) => {
  const files = await get(sortedS3Files)
  const selectedCourse = get(selectedCourseAtom)

  if (!selectedCourse) return

  const unitData = files[selectedCourse]
  return Object.keys(unitData)
    .sort(stringOrNumberSort)
    .map((key) => unitData[key])
})

export const baseParamsForSelectedCourseAtom = atom(async (get) => {
  const files = await get(sourceFilesForSelectedCourseAtom)
  if (!files) return
  const urls = files.map(({ URL }) => URL)
  const downloaded = (
    await Promise.all(urls.map((url) => htmlDocForUnitUrl(url)))
  ).filter((d) => !!d) as Document[]
  return downloaded.map((htmlContent) => {
    const metadata = getDocMetadata(htmlContent)
    return {
      htmlContent,
      metadata,
    }
  })
})

export const gDriveFilesForIdAtom = atom<Record<string, GDriveFileWithPath[]>>(
  {},
)

function includeFile({ namePath, name: dirty }: GDriveFileWithPath) {
  const name = cleanFileName(dirty)
  if (namePath.includes('Lessons/')) return true
  if (isMaterialsFile({ name, namePath })) {
    return true
  }
  return name.endsWith('.ASMT') || name.endsWith('.TBK')
}

export const auxContentGDriveFilesAtom = atom(async (get) => {
  const filesById = get(gDriveFilesForIdAtom)
  const allFiles = Object.values(filesById).reduce(
    (acc, group) => [...acc, ...group],
    [],
  )
  return allFiles.reduce<GDriveCourseFiles>((acc, file) => {
    if (!includeFile(file)) return acc
    const [courseCode, lessonCode, typeCode] = file.name
      .split('.')
      .map((s) => s.trim())
    if (!courseCode || !lessonCode) return acc
    if (!(courseCode in acc)) {
      acc[courseCode] = {}
    }
    if (!(lessonCode in acc[courseCode])) {
      acc[courseCode][lessonCode] = {}
    }
    const [typeKey] = (typeCode ?? lessonCode).split(' ')
    acc[courseCode][lessonCode][typeKey] = file
    return acc
  }, {})
})

export const gDriveFilesForSelectedCourseAtom = atom(async (get) => {
  const gDriveFiles = await get(auxContentGDriveFilesAtom)
  const baseParams = await get(baseParamsForSelectedCourseAtom)
  if (!baseParams) return
  return baseParams.map<{
    fileMap: GDriveUnitFiles | undefined
    courseKey: string
  }>(({ metadata }) => {
    const courseKey = metadata.courseCode.split('/').pop() ?? ''
    const fileMap = gDriveFiles?.[courseKey]
    return {
      fileMap,
      courseKey,
    }
  })
})

export type GDriveLessonFiles = Record<string, GDriveFileWithPath>
export type GDriveUnitFiles = Record<string, GDriveLessonFiles>
type GDriveCourseFiles = Record<string, GDriveUnitFiles>

function cleanFileName(name: string) {
  return name.split('.').slice(0, 3).join('.')
}

// This is course id mapped to lesson id mapped to parsed gdoc content
export const gDriveContentForCourse = atom<
  Record<string, Record<string, ParsedGoogleDocType>>
>({})

export const gDriveContentFilesForSelectedCourseAtom = atom((get) => {
  const selectedCourse = get(selectedCourseAtom)
  const content = get(gDriveContentForCourse)
  if (!(selectedCourse in content)) return undefined
  return content[selectedCourse]
})

export const videoIdMapAtom = atom(async (get) => {
  const { credential } = get(googleUserAtom)
  if (!credential?.accessToken) return
  const doc = await getSheet(
    credential.accessToken,
    '1KM1U8cZja3JNUxFE2RnzWTvH0z7qAeO5OG68iGM5IL0',
  )
  const result = (
    await parseCsvData<{ identifier: string; url: string }>(doc)
  ).reduce((acc, { identifier, url }) => {
    return {
      ...acc,
      [identifier]: url,
    }
  }, {})
  return result
})

type IconSheetDataType = {
  googleDocCode: string
  ['kiddomS3UrlTo 2025Icons']: string
  ['png &SvgFileName']: string
}

export const iconSheetAtom = atom(async (get) => {
  const { credential } = get(googleUserAtom)
  if (!credential?.accessToken) return []
  const doc = await getSheet(
    credential.accessToken,
    '1eD5CuCUeu36VGEySJKDTs_mLEonwXQrDoKO-qWbQyos',
  )
  const result = await parseCsvData<IconSheetDataType>(doc)
  return result
})

export const phenomIconMapAtom = atom(async (get) => {
  const iconData = await get(iconSheetAtom)
  return iconData.reduce<Record<string, string>>((acc, data) => {
    const docCode = data.googleDocCode
    if (docCode.includes('phenomena-types:')) {
      return {
        ...acc,
        [docCode.split(' ').pop()!]: data['kiddomS3UrlTo 2025Icons'],
      }
    }
    return {
      ...acc,
      [data['png &SvgFileName']]: data['kiddomS3UrlTo 2025Icons'],
    }
  }, {})
})
